import { useCallback, useEffect, useState } from 'react';
import { Bot, Copy, Plus, RefreshCw, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { AgentEnrollmentPanel } from './AgentEnrollmentPanel';
import { agentApiUrl, agentScopeLabels, type AgentScope } from '@/lib/agent-access';

type Scope = AgentScope;
type Credential = {
  id: string; name: string; token_prefix: string; scopes: Scope[];
  expires_at: string; revoked_at: string | null;
};
type Response<T> = { success: boolean; data?: T; error?: string };
const labels = agentScopeLabels;

export function AgentAccessPanel() {
  const confirm = useConfirmDialog();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [scopes, setScopes] = useState<Scope[]>(['social:read', 'social:draft']);
  const [token, setToken] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Response<Credential[]>>('/agent-access');
      if (!res.success || !res.data) throw new Error(res.error || 'Agentenzugänge konnten nicht geladen werden.');
      setCredentials(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Agentenzugänge konnten nicht geladen werden.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<Response<Credential & { token: string }>>('/agent-access', { name, scopes, expires_days: days });
      if (!res.success || !res.data) throw new Error(res.error || 'Zugang konnte nicht erstellt werden.');
      setToken(res.data.token);
      void load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Zugang konnte nicht erstellt werden.'); }
    finally { setBusy(false); }
  }

  async function revoke(credential: Credential) {
    if (!await confirm({ title: 'Agentenzugang widerrufen?', description: `${credential.name} verliert sofort den Zugriff.`, confirmText: 'Widerrufen', variant: 'destructive' })) return;
    setBusy(true);
    try {
      const res = await api.delete<Response<never>>(`/agent-access/${credential.id}`);
      if (!res.success) throw new Error(res.error || 'Widerruf fehlgeschlagen.');
      toast.success('Agentenzugang widerrufen.');
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Widerruf fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  return (
    <><Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5" />Agentenzugänge</CardTitle>
          <p className="text-sm text-muted">Externe Agenten für Social Content. Rechte gelten innerhalb der Berechtigungen des Erstellers.</p>
        </div>
        <div className="flex flex-wrap gap-2"><Button variant="outline" size="icon" disabled={loading || busy} aria-label="Agentenzugänge aktualisieren" onClick={() => void load()}><RefreshCw className="h-4 w-4" /></Button><Button variant="outline" onClick={() => { setName(''); setDays(30); setScopes(['social:read', 'social:draft']); setToken(null); setOpen(true); }}><Plus className="h-4 w-4" />Zugang erstellen</Button></div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-danger">{error}<Button size="sm" variant="outline" onClick={() => void load()}><RefreshCw className="h-4 w-4" />Erneut laden</Button></div>
          : loading ? <p className="text-sm text-muted" role="status">Zugänge werden geladen…</p>
          : credentials.length === 0 ? <p className="text-sm text-muted">Noch keine externen Agenten verbunden.</p>
          : credentials.map(credential => {
            const expired = new Date(credential.expires_at).getTime() <= Date.now();
            const inactive = Boolean(credential.revoked_at) || expired;
            return <div key={credential.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2"><span className="font-medium text-foreground">{credential.name}</span><Badge variant={inactive ? 'secondary' : 'success'}>{credential.revoked_at ? 'Widerrufen' : expired ? 'Abgelaufen' : 'Aktiv'}</Badge></div>
                <div className="flex flex-wrap gap-1">{credential.scopes.map(scope => <Badge key={scope} variant="outline">{labels[scope]}</Badge>)}</div>
                <p className="text-xs text-muted">{credential.token_prefix}… · Gültig bis {new Date(credential.expires_at).toLocaleDateString('de-CH')}</p>
              </div>
              {!inactive && <Button size="sm" variant="outline" disabled={busy} onClick={() => void revoke(credential)}><ShieldOff className="h-4 w-4" />Widerrufen</Button>}
            </div>;
          })}
        <p className="text-xs text-muted">Agenten können keine Freigaben erteilen. Kontoanmeldungen und Plattformschlüssel bleiben in Leftclick.</p>
      </CardContent>
      <Dialog open={open} onOpenChange={next => { if (busy) return; setOpen(next); if (!next) setToken(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{token ? 'Agentenschlüssel erstellt' : 'Agentenzugang erstellen'}</DialogTitle><DialogDescription>{token ? 'Kopiere den Schlüssel jetzt in deine Agentenplattform. Er wird nur einmal angezeigt.' : 'Ein eigener Schlüssel mit Ablaufdatum. Standardmäßig darf der Agent Entwürfe vorbereiten.'}</DialogDescription></DialogHeader>
          {token ? <div className="space-y-4">
            <Label htmlFor="agent-token">Agentenschlüssel</Label>
            <Input id="agent-token" value={token} readOnly className="font-mono text-xs" autoComplete="off" />
            <Button onClick={async () => { try { await navigator.clipboard.writeText(token); toast.success('Schlüssel kopiert.'); } catch { toast.error('Bitte den Schlüssel markieren und kopieren.'); } }}><Copy className="h-4 w-4" />Kopieren</Button>
            <p className="text-sm text-muted">Als Bearer-Token verwenden. Die verfügbaren Aktionen liefert <code className="text-xs">GET /api/agent-access/manifest</code>.</p>
            <div className="space-y-2"><Label htmlFor="agent-mcp-url">MCP-Verbindung</Label><Input id="agent-mcp-url" value={agentApiUrl('/mcp')} readOnly className="font-mono text-xs" /><p className="text-xs text-muted">Streamable HTTP · Bearer-Schlüssel im Agenten-Client hinterlegen.</p></div>
            <Button variant="outline" onClick={() => { setOpen(false); setToken(null); }}>Fertig</Button>
          </div> : <form className="space-y-4" onSubmit={e => { e.preventDefault(); void create(); }}>
            <div className="space-y-2"><Label htmlFor="agent-access-name">Name</Label><Input id="agent-access-name" value={name} onChange={e => setName(e.target.value)} minLength={2} maxLength={120} required placeholder="Content-Agent" /></div>
            <div className="space-y-2"><Label htmlFor="agent-access-days">Gültig für (Tage)</Label><Input id="agent-access-days" type="number" min={1} max={90} step={1} value={days} onChange={e => setDays(Number(e.target.value))} required /></div>
            <fieldset className="space-y-2"><legend className="mb-2 text-sm font-medium">Rechte</legend>{(Object.keys(labels) as Scope[]).map(scope => <label key={scope} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={scopes.includes(scope)} onChange={e => setScopes(previous => e.target.checked ? [...previous, scope] : previous.filter(item => item !== scope))} className="accent-primary" />{labels[scope]}</label>)}</fieldset>
            {scopes.includes('social:publish') && <p className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning">Der Agent darf Posts veröffentlichen und planen. Die Social-Hub-Freigaberegel gilt weiterhin.</p>}
            {scopes.includes('browser:execute') && <p className="text-sm text-muted">Nur für deinen lokalen Browser-Runner. Anmeldung und Veröffentlichung werden dort von dir bestätigt.</p>}
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Abbrechen</Button><Button type="submit" disabled={busy || name.trim().length < 2 || scopes.length === 0}>{busy ? 'Wird erstellt…' : 'Schlüssel erstellen'}</Button></div>
          </form>}
        </DialogContent>
      </Dialog>
    </Card><AgentEnrollmentPanel /></>
  );
}
